import {
  Checkbox,
  CheckboxProps,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Theme
} from "@mui/material";
import { createStyles, withStyles } from "@mui/styles";
import { Field, FieldProps, Form, Formik } from "formik";
import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { ANSWER_TYPES, BOOKING_SOURCE, IBookingCreateInput, IBookingQuestionInput } from "../../../../../reducers/bookings/types";
import { IAppState } from "../../../../../store";
import { getLocalizedBookingSyntex } from "../../../../../utils/localized.syntex";
import { IBookingQuestion } from "../../../AppSettings/types";
import styles from './index.module.css';

const StyledInput = withStyles((theme: Theme) =>
  createStyles({
    root: {
      '& .MuiOutlinedInput-multiline': {
        padding: '0px 4px',
        height: 60
      },
      '& .MuiOutlinedInput-root fieldset': {
        borderColor: '#e2e2e2',
        borderRadius: 8
      },
      '& .MuiSelect-select:focus': {
        backgroundColor: 'transparent'
      },
      '& .MuiSelect-select ~ fieldset': {
        border: 0
      },
    },
  }),
)(TextField);

const ColoredCheckbox = withStyles({
  root: {
    color: 'var(--theme-primary)',
    '&$checked': {
      color: 'var(--theme-primary)',
    },
  },
  checked: {},
})((props: CheckboxProps) => <Checkbox color="default" {...props} />);

interface IBookingQuestionsProps {
  booking: IBookingCreateInput,
  formikRef: any
}

export const BookingQuestions: React.FC<IBookingQuestionsProps> = (props) => {
  const { currentBranch, currentOrganisation } = useSelector((state: IAppState) => state.userReducer);
  const [preBookingQuestions, setPreBookingQuestions] = useState<IBookingQuestion[]>([])

  let initialValues = useMemo(() => {
    return {
      ...props.booking,
      preBookingQuestions: preBookingQuestions.filter(q => q.B2B),
    }
  }, [preBookingQuestions]);
  const validateAnswer = (value: any, required: boolean, expected?: string) => {
    let error;
    if (required) {
      if (expected === "TRUE" && !value) {
        error = "Error"
      }
      if (expected === "FALSE" && value) {
        error = "Error"
      }
      if (expected === "TEXT" && (!value || value.trim() === "")) {
        error = "Error"
      }
      if (expected === "YES" && value !== "YES") {
        error = "Error"
      }
      if (expected === "NO" && value !== "NO") {
        error = "Error"
      }
    }
    return error;
  }

  useEffect(() => {
    const bookingQuestions = props.booking.preBookingQuestions || [];
    if (
      !currentBranch &&
      !bookingQuestions.length
    ) {
      setPreBookingQuestions([]);
      return;
    }

    const branchQuestions = currentBranch.preBookingQuestions?.filter(bq => bq.B2B) || [];

    if (!bookingQuestions.length) {
      setPreBookingQuestions(branchQuestions);
      return;
    }

    const mergedQuestions: IBookingQuestion[] = mergePreBookingQuestions(branchQuestions, bookingQuestions);
    setPreBookingQuestions(mergedQuestions);
  }, []);

  function mergePreBookingQuestions(
    branchQuestions: IBookingQuestion[],
    bookingQuestions: IBookingQuestionInput[]
  ): IBookingQuestion[] {
    let merged: IBookingQuestion[] = branchQuestions

    for (const bq of bookingQuestions) {
      const idx = merged.findIndex(q => q.question === bq.question)
      const answer = bq?.answer !== "false" ? bq?.answer : ""
      if (idx <= -1) {
        merged.push({
          question: bq.question,
          answerType: bq.answerType,
          deleted: true,
          required: true,
          answer: answer,
          B2B: true,
          B2B2C: true
        });
      }
      else {
        merged[idx] = { ...merged[idx], answer: answer };
      }
    }
    return merged;
  }

  const staticBookingAnswerRef = useRef<(string | boolean | undefined)[]>([]);
  useEffect(() => {
    initialValues.preBookingQuestions.map((ele:IBookingQuestionInput,idx: number) => {
      staticBookingAnswerRef.current[idx] = ele.answer;
    })
  },[])
  return (
    <>
      {preBookingQuestions.length > 0 && (
        <Grid container>
          <Paper style={{ width: "100%", marginTop: "20px", marginBottom: 20 }}>
            <Grid item style={{ padding: 20 }}>
              <Formik
                initialValues={initialValues}
                validateOnMount
                onSubmit={() => { }}
                innerRef={props.formikRef}
              >
                {(formikProps) => (
                  <Form>
                    {preBookingQuestions.length > 0 && (
                      <Grid container xs={12}>
                        <Grid item xs={12} style={{ marginTop: 10 }}>
                          <Typography variant="h4">{`${getLocalizedBookingSyntex(currentOrganisation.address.country)} questions`}</Typography>
                        </Grid>
                        <Grid container spacing={1} item xs={12} style={{ marginTop: 16 }}>
                          {preBookingQuestions.map((preBookingQuesiton: IBookingQuestion, idx: number) => {
                            if (!preBookingQuesiton.B2B) {
                              return null
                            }
                            switch (preBookingQuesiton.answerType) {
                              case ANSWER_TYPES.CHECKBOX:
                                return (
                                  <Grid item xs={12} sm={6} style={{ marginTop: 16 }}>
                                    <Field
                                      name={`preBookingQuestions[${idx}].answer`}
                                      validate={(value: any) => validateAnswer(value, preBookingQuesiton.required, 'TRUE')} >
                                      {({ field, meta: { touched, error }, }: FieldProps) => (
                                        <div className={`${styles.validation} ${(touched && Boolean(error)) ? styles.error : ''}`} style={{ padding: 12 }}>
                                          {staticBookingAnswerRef.current[idx] ? (<FormControlLabel
                                            control={
                                              <ColoredCheckbox
                                                {...field}
                                                checked={field.value}
                                                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                                  formikProps.setFieldValue(`preBookingQuestions[${idx}].answer`, event.target.checked);
                                                }}
                                                size="small"
                                                color="primary"
                                                inputProps={{ 'aria-label': 'secondary checkbox' }}
                                              />
                                            }
                                            label={`${preBookingQuesiton.question} ${preBookingQuesiton.required ? "*" : ""}`}
                                            disabled={preBookingQuesiton.deleted || formikProps.values.source === BOOKING_SOURCE.B2B2C}
                                          />) : (<FormControlLabel
                                            control={
                                              <ColoredCheckbox
                                                {...field}
                                                checked={field.value}
                                                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                                  formikProps.setFieldValue(`preBookingQuestions[${idx}].answer`, event.target.checked);
                                                }}
                                                size="small"
                                                color="primary"
                                                inputProps={{ 'aria-label': 'secondary checkbox' }}
                                                disabled={preBookingQuesiton.deleted}
                                              />
                                            }
                                            label={`${preBookingQuesiton.question} ${preBookingQuesiton.required ? "*" : ""}`}
                                          />)
                                          }
                                          {touched && Boolean(error) && <Typography variant="body1" style={{ color: '#D13135' }}>{preBookingQuesiton.errorMessage}</Typography>}
                                        </div>
                                      )}
                                    </Field>
                                  </Grid>
                                )
                              case ANSWER_TYPES.DROP_DOWN:
                                return (
                                  <Grid item xs={12} sm={6} style={{ marginTop: 16 }}>
                                    <Field name={`preBookingQuestions[${idx}].answer`} validate={(value: any) => validateAnswer(value, preBookingQuesiton.required, "TEXT")}>
                                      {({ field, meta: { touched, error }, }: FieldProps) => (
                                        <>
                                          <div className={`${styles.bookingQuestion} ${touched && (Boolean(error)) ? styles.error : ''}`}>
                                            <div className={styles.rowFlex}>
                                              <div style={{ display: 'flex', flex: 1, paddingRight: 15 }}>
                                                <Typography>{`${preBookingQuesiton.question} ${preBookingQuesiton.required ? "*" : ""}`}</Typography>
                                              </div>
                                              {staticBookingAnswerRef.current[idx] ? (<div className={styles.dropdownWrap}>
                                                <StyledInput
                                                  select
                                                  {...field}
                                                  fullWidth
                                                  variant="outlined"
                                                  error={touched && Boolean(error)}
                                                  disabled={preBookingQuesiton.deleted || formikProps.values.source === BOOKING_SOURCE.B2B2C}
                                                >
                                                  {preBookingQuesiton.options && preBookingQuesiton.options.map((option) => (
                                                    <MenuItem key={option} value={option}>
                                                      {option}
                                                    </MenuItem>
                                                  ))}
                                                </StyledInput>
                                              </div>) : (<div className={styles.dropdownWrap}>
                                                <StyledInput
                                                  select
                                                  {...field}
                                                  fullWidth
                                                  variant="outlined"
                                                  error={touched && Boolean(error)}
                                                  disabled={preBookingQuesiton.deleted}
                                                >
                                                  {preBookingQuesiton.options && preBookingQuesiton.options.map((option) => (
                                                    <MenuItem key={option} value={option}>
                                                      {option}
                                                    </MenuItem>
                                                  ))}
                                                </StyledInput>
                                              </div>)}
                                            </div>
                                            {touched && Boolean(error) && <Typography variant="body1" style={{ color: '#D13135' }}>{preBookingQuesiton.errorMessage}</Typography>}
                                          </div>
                                        </>
                                      )}
                                    </Field>
                                  </Grid>
                                )
                              case ANSWER_TYPES.YES_NO:
                                return (
                                  <Grid item xs={12} sm={6} style={{ marginTop: 16 }}>
                                    <Field
                                      name={`preBookingQuestions[${idx}].answer`}
                                      validate={(value: any) => validateAnswer(value, preBookingQuesiton.required, preBookingQuesiton.expectedAnswer)}
                                    >
                                      {({ field, meta: { touched, error }, }: FieldProps) => (
                                        <RadioGroup {...field} onClick={field.onBlur}>
                                          <div className={`${styles.bookingQuestion} ${touched && (Boolean(error)) ? styles.error : ''}`}>
                                            <div className={styles.rowFlex}>
                                              <div style={{ display: 'flex', flex: 1, paddingRight: 15 }}>
                                                <Typography>{`${preBookingQuesiton.question} ${preBookingQuesiton.required ? "*" : ""}`}</Typography>
                                              </div>
                                              {staticBookingAnswerRef.current[idx] ? (<div className={styles.yesnoWrap}>
                                                <FormControlLabel
                                                  value="YES"
                                                  control={<Radio style={{ display: 'none' }} />}
                                                  label="YES"
                                                  className={`${styles.yesnoButton} ${styles.left} ${field.value === "YES" ? styles.active : ''}`}
                                                  disabled={preBookingQuesiton.deleted || formikProps.values.source === BOOKING_SOURCE.B2B2C}
                                                />
                                                <FormControlLabel
                                                  value="NO"
                                                  control={<Radio style={{ display: 'none' }} />}
                                                  label="NO"
                                                  className={`${styles.yesnoButton} ${styles.right} ${field.value === "NO" ? styles.active : ''}`}
                                                  disabled={preBookingQuesiton.deleted || formikProps.values.source === BOOKING_SOURCE.B2B2C}
                                                />
                                              </div>
                                              ) : (<div className={styles.yesnoWrap}>
                                                <FormControlLabel
                                                  value="YES"
                                                  control={<Radio style={{ display: 'none' }} />}
                                                  label="YES"
                                                  className={`${styles.yesnoButton} ${styles.left} ${field.value === "YES" ? styles.active : ''}`}
                                                  disabled={preBookingQuesiton.deleted}
                                                />
                                                <FormControlLabel
                                                  value="NO"
                                                  control={<Radio style={{ display: 'none' }} />}
                                                  label="NO"
                                                  className={`${styles.yesnoButton} ${styles.right} ${field.value === "NO" ? styles.active : ''}`}
                                                  disabled={preBookingQuesiton.deleted}
                                                />
                                              </div>)
                                              }

                                            </div>
                                            {touched && Boolean(error) && <Typography variant="body1" style={{ color: '#D13135' }}>{preBookingQuesiton.errorMessage}</Typography>}
                                          </div>
                                        </RadioGroup>
                                      )
                                      }
                                    </Field>
                                  </Grid>
                                )
                              case ANSWER_TYPES.TEXT:
                                return (
                                  <Grid item xs={12} sm={6}>
                                    <Field name={`preBookingQuestions[${idx}].answer`} validate={(value: any) => validateAnswer(value, preBookingQuesiton.required, 'TEXT')}>
                                      {({ field, meta: { touched, error }, }: FieldProps) => (
                                        <>
                                          <Typography variant="body1">{`${preBookingQuesiton.question} ${preBookingQuesiton.required ? "*" : ""}`}</Typography>
                                          {staticBookingAnswerRef.current[idx] ? (<StyledInput
                                            variant="outlined"
                                            {...field}
                                            multiline
                                            fullWidth
                                            rows={2}
                                            error={touched && Boolean(error)}
                                            disabled={preBookingQuesiton.deleted || formikProps.values.source === BOOKING_SOURCE.B2B2C}
                                          />) : (<StyledInput
                                            variant="outlined"
                                            {...field}
                                            multiline
                                            fullWidth
                                            rows={2}
                                            error={touched && Boolean(error)}
                                            disabled={preBookingQuesiton.deleted}
                                          />)
                                          }
                                          <Typography variant="body1" style={{ color: '#D13135', padding: '5px 15px 5px 15px' }}>{touched && Boolean(error) && preBookingQuesiton.errorMessage}</Typography>
                                        </>
                                      )}
                                    </Field>
                                  </Grid>
                                )
                            }
                          })}
                        </Grid>
                      </Grid>
                    )}
                  </Form>
                )}
              </Formik>
            </Grid>
          </Paper>
        </Grid>
      )}
    </>
  )
}
